// src/contexts/CameraContext.js
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const CameraContext = createContext();

export const CameraProvider = ({ children }) => {
  const [cameraState, setCameraState] = useState({
    position: { x: 300, y: 300, z: 300 },
    target: { x: 0, y: 0, z: 0 },
    up: { x: 0, y: 1, z: 0 },
    zoom: 1,
    lastUpdate: Date.now()
  });
  
  const registeredCameras = useRef(new Map());
  const registeredControls = useRef(new Map());
  const isUpdating = useRef(false);
  const lastPosition = useRef(new Map());
  const updateDebounce = useRef(null);

  const registerCamera = useCallback((id, camera, controls) => {
    if (!camera || !controls) {
      console.log(`Unregistering camera for ${id}`);
      registeredCameras.current.delete(id);
      registeredControls.current.delete(id);
      lastPosition.current.delete(id);
      return () => {};
    }

    console.log(`Registering camera for ${id}`);
    registeredCameras.current.set(id, camera);
    registeredControls.current.set(id, controls);
    
    // 同步初始状态
    camera.position.set(
      cameraState.position.x,
      cameraState.position.y,
      cameraState.position.z
    );
    
    if (controls.target) {
      controls.target.set(
        cameraState.target.x,
        cameraState.target.y,
        cameraState.target.z
      );
    }
    
    camera.up.set(
      cameraState.up.x,
      cameraState.up.y,
      cameraState.up.z
    );
    
    if (cameraState.zoom) {
      camera.zoom = cameraState.zoom;
    }
    
    camera.updateProjectionMatrix();
    controls.update();
    
    return () => {
      registeredCameras.current.delete(id);
      registeredControls.current.delete(id);
      lastPosition.current.delete(id);
    };
  }, [cameraState]);

  const updateCameraState = useCallback((sourceId, camera, controls) => {
    if (isUpdating.current || !camera || !controls) return;
    
    // 检查位置是否真的改变了
    const lastPos = lastPosition.current.get(sourceId);
    const currentPos = {
      x: camera.position.x,
      y: camera.position.y,
      z: camera.position.z,
      targetX: controls.target?.x || 0,
      targetY: controls.target?.y || 0,
      targetZ: controls.target?.z || 0,
      zoom: camera.zoom || 1
    };
    
    // 如果位置没有显著变化，跳过更新
    const threshold = 0.01;
    if (lastPos && 
        Math.abs(lastPos.x - currentPos.x) < threshold &&
        Math.abs(lastPos.y - currentPos.y) < threshold &&
        Math.abs(lastPos.z - currentPos.z) < threshold &&
        Math.abs(lastPos.targetX - currentPos.targetX) < threshold &&
        Math.abs(lastPos.targetY - currentPos.targetY) < threshold &&
        Math.abs(lastPos.targetZ - currentPos.targetZ) < threshold &&
        Math.abs(lastPos.zoom - currentPos.zoom) < threshold) {
      return;
    }
    
    lastPosition.current.set(sourceId, currentPos);
    
    // 清除之前的防抖
    if (updateDebounce.current) {
      clearTimeout(updateDebounce.current);
    }
    
    // 使用防抖来减少更新频率
    updateDebounce.current = setTimeout(() => {
      isUpdating.current = true;

      const newState = {
        position: {
          x: camera.position.x,
          y: camera.position.y,
          z: camera.position.z
        },
        target: controls.target ? {
          x: controls.target.x,
          y: controls.target.y,
          z: controls.target.z
        } : { x: 0, y: 0, z: 0 },
        up: {
          x: camera.up.x,
          y: camera.up.y,
          z: camera.up.z
        },
        zoom: camera.zoom || 1,
        lastUpdate: Date.now()
      };
      
      setCameraState(newState);
      console.log(`Camera state updated from ${sourceId}`, newState);
      
      // 同步到其他相机
      requestAnimationFrame(() => {
        registeredCameras.current.forEach((cam, id) => {
          if (id !== sourceId && cam) {
            const ctrl = registeredControls.current.get(id);
            
            cam.position.copy(camera.position);
            cam.up.copy(camera.up);
            cam.zoom = camera.zoom || 1;
            cam.updateProjectionMatrix();
            
            if (ctrl) {
              if (ctrl.target && controls.target) {
                ctrl.target.copy(controls.target);
              }
              ctrl.update();
            }
          }
        });
        
        isUpdating.current = false;
      });
    }, 16); // 约60fps的防抖延迟
  }, []);

  const value = {
    cameraState,
    registerCamera,
    updateCameraState
  };

  return (
    <CameraContext.Provider value={value}>
      {children}
    </CameraContext.Provider>
  );
};

export const useCameraSync = () => {
  const context = useContext(CameraContext);
  if (!context) {
    throw new Error('useCameraSync must be used within a CameraProvider');
  }
  return context;
};